import Icon from '@mdi/react';
import { pdsTv } from '@purinanbm/pds-ui';
import Downshift, { DownshiftProps } from 'downshift';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import capitalize from 'lodash/capitalize';
import { matchSorter } from 'match-sorter';
import React, { useRef } from 'react';
import { mdiClose } from 'src/assets/icons/mdiIcons';

/**
 * NOTES ON SCROLL INTO VIEW
 *
 * In this component you will find scroll-m-0 on the individual items in the list.
 * This is because downshift uses compute-scroll-into-view to scroll the list into view.
 * compute-scroll-into-view uses the scrollMargin on the nearest dom element that specifies it or atleast it seems that way.
 *
 * This is the commit that added the scrollIntoView margin functionality https://github.com/scroll-into-view/compute-scroll-into-view/pull/906/files
 * This is the use inside downshift https://github.com/downshift-js/downshift/blob/59d3498faafd3c3b4832a20dab7485502ce67284/src/downshift.js#L247
 * In which it applies it to the node which is the individual item in the list.
 **/

export type ComboBoxItem = {
  value: string;
  id: string;
  category?: string;
};

export type ComboboxProps = {
  items: ComboBoxItem[];
  categories?: string[];
};

const getItems = (items: ComboBoxItem[], value: string) =>
  value ? matchSorter(items, value, { keys: ['value', 'category'] }) : items;

const Combobox: React.FC<DownshiftProps<ComboBoxItem> & ComboboxProps> = function (props) {
  const itemStyles = pdsTv({
    base: 'pds-box-border pds-cursor-pointer pds-scroll-m-0 pds-bg-surface pds-px-4 pds-py-3.5',
    variants: {
      highlighted: {
        true: 'pds-bg-focus pds-text-white',
      },
    },
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const { items, categories = [] } = props;
  const { t } = useTranslation();
  const hasCategories = Boolean(categories.length > 0);
  const ListBoxWrapper = hasCategories ? 'div' : 'ul';

  return (
    <Downshift {...props} itemToString={item => (item ? capitalize(item.value) : '')}>
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getRootProps,
        clearSelection,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
      }) => (
        <form onSubmit={e => e.preventDefault()} className=" mb-3">
          <div className="pds-mx-auto pds-w-full pds-max-w-[764px]">
            {/* label association is passed in getLabelProps */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label {...getLabelProps()} className="pds-mb-2 pds-text-body-md pds-font-bold">
              {t('Search Ingredients')}
            </label>
          </div>
          <div
            {...getRootProps()}
            className="pds-relative pds-mx-auto pds-my-0 pds-flex pds-w-full pds-max-w-[764px] pds-items-center pds-justify-center"
          >
            <input
              className="pds-w-full pds-rounded pds-border-thick pds-border-surface-text pds-p-4 pds-text-surface-text placeholder:pds-text-gray-700 hover:pds-bg-surface-layer focus:pds-bg-surface focus:pds-outline-focus"
              {...getInputProps({
                ref: inputRef,
              })}
            />
            {isOpen || selectedItem ? (
              <button
                tabIndex={selectedItem ? 0 : -1}
                type="button"
                className="pds-absolute pds-right-4"
                onClick={() => {
                  clearSelection();
                  inputRef.current?.focus();
                }}
              >
                <Icon title={t('Clear Search')} path={mdiClose} size={1} />
              </button>
            ) : null}
          </div>
          <ListBoxWrapper
            tabIndex={-1}
            className="pds-mx-auto pds-my-0 pds-mt-1 pds-box-border pds-max-h-[479px] pds-w-full pds-max-w-[764px] pds-overflow-y-scroll pds-rounded pds-p-0 pds-shadow-weak"
            {...getMenuProps()}
          >
            {isOpen && !hasCategories
              ? getItems(items, inputValue || '').map((item, index: number) => (
                  <li
                    className={itemStyles({
                      highlighted: highlightedIndex === index,
                    })}
                    {...getItemProps({
                      key: item.id,
                      index,
                      item,
                    })}
                  >
                    {item.value}
                  </li>
                ))
              : null}
            {isOpen && hasCategories
              ? categories.map(category => {
                  const sortedItems = getItems(items, inputValue || '').sort(
                    // Categories must contain an item so we can bang category.
                    (a, b) => categories.indexOf(a.category!) - categories.indexOf(b.category!),
                  );
                  const categoryItems = sortedItems.filter(
                    (item: ComboBoxItem) => item.category === category,
                  );
                  if (categoryItems.length) {
                    return (
                      <ul
                        className="pds-m-0 pds-p-0"
                        key={`cat-${category}`}
                        role="group"
                        aria-labelledby={`cat-${category}`}
                      >
                        <li
                          className="pds-box-border pds-scroll-m-0 pds-bg-surface-line pds-px-4 pds-py-3.5 pds-font-bold"
                          id={`cat-${category}`}
                          role="presentation"
                        >
                          {category}
                        </li>
                        {categoryItems.map(item => (
                          <li
                            className={itemStyles({
                              highlighted: highlightedIndex === sortedItems.indexOf(item),
                            })}
                            {...getItemProps({
                              key: item.id,
                              index: sortedItems.indexOf(item),
                              item,
                            })}
                          >
                            {item.value}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })
              : null}
          </ListBoxWrapper>
        </form>
      )}
    </Downshift>
  );
};

export default Combobox;
